import React, { useState, useEffect, useRef, useMemo } from 'react';
import type { User, PathwayBlock, OjtSession, OjtMode, PathwayProgress } from '../../../types';
import { DataService } from '../../../services/firebase';
import { useTranslation } from 'react-i18next';
import { Handshake } from './Handshake';
import { PreStartChecklist } from './PreStartChecklist';
import { ObjectiveExecution } from './ObjectiveExecution';
import { SessionSignOff } from './SessionSignOff';
import { ModeBanner } from './ModeBanner';
import { ModePicker } from './ModePicker';
import { ModeChangeSignOff } from './ModeChangeSignOff';
import { ChevronRight } from 'lucide-react';

type FlowStep = 'loading' | 'lead-hand' | 'handshake' | 'pre-start' | 'objectives' | 'comments' | 'sign-off';

interface OjtSessionFlowProps {
  user: User;
  block: PathwayBlock;
  pathwayId: string;
  sessionId: string | null;
  pathwayBlocks?: PathwayBlock[];
  onComplete: () => void;
  onCancel: () => void;
}

export const OjtSessionFlow: React.FC<OjtSessionFlowProps> = ({
  user,
  block,
  pathwayId,
  sessionId,
  pathwayBlocks,
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<FlowStep>(sessionId ? 'loading' : 'lead-hand');
  const [session, setSession] = useState<OjtSession | null>(null);
  const [leadHands, setLeadHands] = useState<any[]>([]);
  const [selectedLeadHand, setSelectedLeadHand] = useState<any>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [generatedPin, setGeneratedPin] = useState<string>('');
  const [pathwayProgress, setPathwayProgress] = useState<PathwayProgress | null>(null);
  const locationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Mode state
  const [isModePickerOpen, setIsModePickerOpen] = useState(false);
  const [pendingModeId, setPendingModeId] = useState<string | null>(null);

  const config = block.ojtConfig;
  const modes: OjtMode[] = config?.modes || [];
  const modesEnabled = modes.length > 0;
  const modeSignOff = config?.modeSignOff;

  const currentMode = useMemo(
    () => modes.find(m => m.id === session?.currentModeId),
    [modes, session?.currentModeId],
  );
  const pendingMode = useMemo(
    () => modes.find(m => m.id === pendingModeId),
    [modes, pendingModeId],
  );

  useEffect(() => {
    // Load lead hands
    if (block.leadHandIds && block.leadHandIds.length > 0) {
      DataService.getLeadHandDetails(block.leadHandIds).then(setLeadHands).catch(console.error);
    }
    // If resuming an existing session, load it from Firestore
    if (sessionId) {
      loadExistingSession(sessionId);
    }
    // Load pathway progress for starting mode lookup
    if (modesEnabled) {
      DataService.getPathwaysForCandidate(user.id)
        .then(progresses => {
          const found = progresses.find(p => p.pathwayId === pathwayId);
          if (found) setPathwayProgress(found);
        })
        .catch(console.error);
    }
  }, [block.leadHandIds, sessionId, modesEnabled, user.id, pathwayId]);

  /** Load an existing session and determine the correct step */
  const loadExistingSession = async (sid: string) => {
    try {
      const allSessions = await DataService.getOjtSessionsForCandidate(user.id);
      const found = allSessions.find(s => s.id === sid);
      if (!found) {
        console.error('Session not found:', sid);
        setStep('lead-hand');
        return;
      }
      setSession(found);
      // Restore the PIN so resumed sessions can still verify pin-based sign-offs (e.g. mode change)
      if (found.handshakePin) setGeneratedPin(found.handshakePin);
      // Determine correct step based on session state
      if (found.status === 'SCHEDULED') {
        if (!found.handshakeVerified && config?.handshake && (config.handshake.pinEnabled || config.handshake.selfieEnabled || config.handshake.qrEnabled)) {
          setStep('handshake');
        } else if (!found.preStartChecklistCompleted && config?.preStartChecklist && config.preStartChecklist.length > 0) {
          setStep('pre-start');
        } else {
          setStep('objectives');
        }
      } else if (found.status === 'IN_PROGRESS') {
        setStep('objectives');
      } else {
        // REVIEW_PENDING or COMPLETED — just show objectives (read-only effectively)
        setStep('objectives');
      }
    } catch (e) {
      console.error('Failed to load session', e);
      setStep('lead-hand');
    }
  };

  // Location tracking
  useEffect(() => {
    if (session && session.status === 'IN_PROGRESS' && config?.locationTrackingEnabled) {
      locationIntervalRef.current = setInterval(async () => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(async (pos) => {
          if (!session.id) return;
          const logEntry = { lat: pos.coords.latitude, lng: pos.coords.longitude, timestamp: Date.now() };
          const updated = {
            ...session,
            locationLog: [...(session.locationLog || []), logEntry],
          };
          await DataService.updateOjtSession(updated);
          setSession(updated);
        });
      }, 60000); // Every 60 seconds
    }
    return () => {
      if (locationIntervalRef.current) clearInterval(locationIntervalRef.current);
    };
  }, [session?.id, session?.status]);

  // Reactive admin review listener — watch for review status changes on the session
  useEffect(() => {
    if (!session?.id) return;
    const unsubscribe = DataService.onOjtSessionChange(session.id, (updatedSession) => {
      // Update local session state with review data from admin
      if (updatedSession.objectiveReviews || updatedSession.reviewStatus) {
        setSession(prev => prev ? {
          ...prev,
          objectiveReviews: updatedSession.objectiveReviews,
          reviewStatus: updatedSession.reviewStatus,
          reviewedBy: updatedSession.reviewedBy,
          reviewedByName: updatedSession.reviewedByName,
          reviewedAt: updatedSession.reviewedAt,
        } : prev);
      }
    });
    return () => unsubscribe();
  }, [session?.id]);

  const generatePin = (): string => {
    return String(Math.floor(1000 + Math.random() * 9000));
  };

  /** Pick the starting mode for a new session — admin-assigned mode wins, falls back to first mode. */
  const pickStartingMode = (): OjtMode | undefined => {
    if (!modesEnabled) return undefined;
    const assignedId = pathwayProgress?.startingModeByBlockId?.[block.id];
    return modes.find(m => m.id === assignedId) || modes[0];
  };

  const handleLeadHandSelected = async () => {
    if (!selectedLeadHand) return;
    setIsCreatingSession(true);
    try {
      const pin = generatePin();
      setGeneratedPin(pin);

      const startingMode = pickStartingMode();
      const now = Date.now();
      const pinEnabled = !!config?.handshake?.pinEnabled;

      // Create the session document
      const newSession: Omit<OjtSession, 'id'> = {
        title: `${block.name} - ${new Date().toLocaleDateString()}`,
        candidateId: user.id,
        candidateName: user.name,
        leadHandId: selectedLeadHand.id,
        leadHandName: selectedLeadHand.name,
        pathwayId,
        blockId: block.id,
        orgId: user.activeOrgId,
        date: now,
        status: 'SCHEDULED',
        elements: [],
        requiredTimeMinutes: (config?.expectedDurationHours || 0) * 60,
        loggedTimeMinutes: 0,
        handshakeType: config?.handshake?.pinEnabled ? 'pin' : config?.handshake?.selfieEnabled ? 'selfie' : config?.handshake?.qrEnabled ? 'qr' : undefined,
        ...(pinEnabled ? { handshakePin: pin } : {}),
        handshakeVerified: false,
        preStartChecklistCompleted: false,
        objectives: config?.objectives?.map(obj => ({
          objectiveId: obj.id,
          label: obj.label,
          isCompleted: false,
          evidence: { photos: [], videos: [], voiceRecordings: [] },
        })) || [],
        ...(startingMode ? {
          startingModeId: startingMode.id,
          currentModeId: startingMode.id,
          modeTimeLog: [{ modeId: startingMode.id, startTime: now }],
        } : {}),
      };

      const createdSession = await DataService.createOjtSession(newSession);
      setSession(createdSession);

      // Send PIN SMS if pin is enabled
      if (config?.handshake?.pinEnabled) {
        await DataService.sendOjtPin(
          selectedLeadHand.id,
          selectedLeadHand.name,
          pin,
          createdSession.id,
          user.name
        );
        setStep('handshake');
      } else if (config?.handshake?.selfieEnabled || config?.handshake?.qrEnabled) {
        setStep('handshake');
      } else if (config?.preStartChecklist && config.preStartChecklist.length > 0) {
        setStep('pre-start');
      } else {
        await startSession(createdSession);
      }
    } catch (e) {
      console.error('Failed to create session', e);
    } finally {
      setIsCreatingSession(false);
    }
  };

  const startSession = async (sess: OjtSession) => {
    const updated = { ...sess, status: 'IN_PROGRESS' as const, startTime: Date.now() };
    await DataService.updateOjtSession(updated);
    setSession(updated);
    setStep('objectives');
  };

  const handleHandshakeComplete = async () => {
    if (!session) return;
    const updated = { ...session, handshakeVerified: true };
    await DataService.updateOjtSession(updated);
    setSession(updated);
    if (config?.preStartChecklist && config.preStartChecklist.length > 0) {
      setStep('pre-start');
    } else {
      await startSession(updated);
    }
  };

  const handlePreStartComplete = async () => {
    if (!session) return;
    const updated = { ...session, preStartChecklistCompleted: true };
    await DataService.updateOjtSession(updated);
    await startSession(updated);
  };

  const handleObjectivesDone = () => {
    if (config?.candidateCommentsEnabled) {
      setStep('comments');
    } else {
      setStep('sign-off');
    }
  };

  const handleCommentsAdded = async (comment: { transcript: string; voiceUrl?: string }) => {
    if (!session) return;
    const updated = {
      ...session,
      candidateComments: [...(session.candidateComments || []), { ...comment, timestamp: Date.now() }],
    };
    await DataService.updateOjtSession(updated);
    setSession(updated);
    setStep('sign-off');
  };

  const handleSessionComplete = async (candidateSignature: string, leadHandSignature?: string) => {
    if (!session) return;
    const endTime = Date.now();
    const loggedMinutes = session.startTime ? Math.floor((endTime - session.startTime) / 60000) : 0;
    // Close any open mode segment so totals are accurate
    const sessionWithClosedMode = DataService.closeOpenModeSegment(session, endTime);
    const updated: OjtSession = {
      ...sessionWithClosedMode,
      status: 'REVIEW_PENDING' as const,
      endTime,
      loggedTimeMinutes: loggedMinutes,
      endSignOff: {
        type: 'SIGNATURE',
        data: candidateSignature,
        timestamp: endTime,
      },
    };
    if (leadHandSignature) {
      (updated as any).leadHandSignOff = { data: leadHandSignature, timestamp: endTime };
    }
    await DataService.updateOjtSession(updated);
    onComplete();
  };

  // ---------- Mode switching ----------
  /** Mode switching is only available while the session is actively in progress. */
  const canSwitchModes = modesEnabled && session?.status === 'IN_PROGRESS';

  const handleModeSelected = async (modeId: string) => {
    if (!canSwitchModes || !session || modeId === session.currentModeId) {
      setIsModePickerOpen(false);
      return;
    }
    setIsModePickerOpen(false);
    if (modeSignOff?.required) {
      setPendingModeId(modeId);
    } else {
      const updated = await DataService.switchOjtSessionMode(session, modeId);
      setSession(updated);
    }
  };

  const handleModeChangeConfirmed = async () => {
    if (!canSwitchModes || !session || !pendingModeId) return;
    const targetId = pendingModeId;
    setPendingModeId(null);
    const updated = await DataService.switchOjtSessionMode(session, targetId);
    setSession(updated);
  };

  /** Whether the persistent mode banner should be visible */
  const showModeBanner = canSwitchModes && step !== 'loading' && step !== 'lead-hand';

  // ---------- Renderers ----------

  const renderStep = () => {
    if (step === 'loading') {
      return (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600"></div>
        </div>
      );
    }

    if (step === 'lead-hand') {
      return (
        <div className="p-4 max-w-2xl mx-auto space-y-4">
          <div className="bg-white rounded-xl border border-aero-200 p-5">
            <h2 className="text-lg font-bold text-aero-900 mb-1">{t('candidate.ojt.selectLeadHand')}</h2>
            <p className="text-sm text-aero-500">{t('candidate.ojt.selectLeadHandDesc')}</p>
          </div>

          <div className="space-y-2">
            {leadHands.length === 0 && (
              <div className="bg-aero-50 rounded-xl border border-aero-100 p-4 text-center">
                <p className="text-sm text-aero-500">No lead hands assigned to this block.</p>
              </div>
            )}
            {leadHands.map((lh) => (
              <button
                key={lh.id}
                onClick={() => setSelectedLeadHand(lh)}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                  selectedLeadHand?.id === lh.id
                    ? 'border-green-500 bg-green-50'
                    : 'border-aero-200 bg-white hover:border-aero-400'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-aero-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-aero-600">{lh.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-aero-900">{lh.name}</p>
                  {lh.email && <p className="text-xs text-aero-400">{lh.email}</p>}
                </div>
                {selectedLeadHand?.id === lh.id && (
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>

          <button
            onClick={handleLeadHandSelected}
            disabled={!selectedLeadHand || isCreatingSession}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-green-500 hover:bg-green-600 disabled:bg-aero-200 disabled:text-aero-400 text-white rounded-xl font-semibold text-sm transition-colors"
          >
            {isCreatingSession ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Creating session...
              </span>
            ) : (
              <>
                {t('candidate.ojt.startSession')}
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      );
    }

    if (step === 'handshake' && session) {
      return (
        <Handshake
          config={config?.handshake || { pinEnabled: false, selfieEnabled: false, qrEnabled: false }}
          pin={generatedPin}
          session={session}
          onComplete={handleHandshakeComplete}
        />
      );
    }

    if (step === 'pre-start' && session) {
      return (
        <PreStartChecklist
          items={config?.preStartChecklist || []}
          onComplete={handlePreStartComplete}
        />
      );
    }

    if (step === 'objectives' && session) {
      return (
        <ObjectiveExecution
          session={session}
          block={block}
          user={user}
          pathwayBlocks={pathwayBlocks}
          currentMode={currentMode}
          onSessionUpdate={(updated) => setSession(updated)}
          onDone={handleObjectivesDone}
        />
      );
    }

    if (step === 'comments' && session) {
      return (
        <div className="p-4 max-w-2xl mx-auto space-y-4">
          <div className="bg-white rounded-xl border border-aero-200 p-5">
            <h2 className="text-lg font-bold text-aero-900 mb-1">{t('candidate.ojt.comments.title')}</h2>
          </div>
          <VoiceCommentRecorder onSave={handleCommentsAdded} onSkip={() => setStep('sign-off')} />
        </div>
      );
    }

    if (step === 'sign-off' && session) {
      return (
        <SessionSignOff
          session={session}
          handshakeConfig={config?.handshake}
          onComplete={handleSessionComplete}
        />
      );
    }

    return null;
  };

  return (
    <>
      {showModeBanner && (
        <ModeBanner
          currentMode={currentMode}
          modeCount={modes.length}
          onTap={() => setIsModePickerOpen(true)}
        />
      )}

      {renderStep()}

      {canSwitchModes && session && (
        <ModePicker
          isOpen={isModePickerOpen}
          modes={modes}
          currentModeId={session.currentModeId}
          modeTimeLog={session.modeTimeLog}
          onSelect={handleModeSelected}
          onClose={() => setIsModePickerOpen(false)}
        />
      )}

      {canSwitchModes && session && pendingMode && modeSignOff?.required && (
        <ModeChangeSignOff
          isOpen={!!pendingModeId}
          session={session}
          fromMode={currentMode}
          toMode={pendingMode}
          config={modeSignOff}
          pin={generatedPin}
          onConfirm={handleModeChangeConfirmed}
          onCancel={() => setPendingModeId(null)}
        />
      )}
    </>
  );
};

// Simple voice comment recorder inline component
const VoiceCommentRecorder: React.FC<{ onSave: (c: { transcript: string; voiceUrl?: string }) => void; onSkip: () => void }> = ({ onSave, onSkip }) => {
  const { t } = useTranslation();
  const [transcript, setTranscript] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<any>(null);

  const startRecording = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Speech recognition not supported in this browser.'); return; }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (e: any) => {
      const t = Array.from(e.results).map((r: any) => r[0].transcript).join('');
      setTranscript(t);
    };
    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
  };

  const stopRecording = () => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-aero-200 p-4">
        <button
          onClick={isRecording ? stopRecording : startRecording}
          className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors mb-3 ${
            isRecording ? 'bg-red-500 text-white' : 'bg-aero-600 text-white hover:bg-aero-700'
          }`}
        >
          {isRecording ? t('candidate.ojt.objectives.stopRecording') : t('candidate.ojt.objectives.record')}
        </button>
        <textarea
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder={t('candidate.ojt.comments.transcript')}
          rows={4}
          className="w-full text-sm border border-aero-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-aero-500 resize-none"
        />
      </div>
      <div className="flex gap-3">
        <button onClick={onSkip} className="flex-1 py-3 border border-aero-200 text-aero-600 rounded-xl font-semibold text-sm hover:bg-aero-50 transition-colors">
          Skip
        </button>
        <button
          onClick={() => onSave({ transcript })}
          disabled={!transcript.trim()}
          className="flex-1 py-3 bg-green-500 hover:bg-green-600 disabled:bg-aero-200 text-white rounded-xl font-semibold text-sm transition-colors"
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
};
